import { useGestures } from "./gestures";
import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import { api, ApiError, getAccounts, switchAccount, type Session } from "../api";
import { AppearancePanel } from "./Appearance";
import { useFlip } from "./flip";
import { loadStickers } from "./Stickers";
import { loadChatLock, lockDialog, toggleLock } from "./ChatLock";
import { FilterChips, filterLabel, inFilter, isFavourite, lists, loadLists, toggleFavourite, toggleInList, type Filter } from "./Lists";
import { palette, shortcutsHelp } from "./Shortcuts";
import { Status, type Chat, type Msg } from "../db";
import * as S from "../state";
import { listTime } from "../util";
import { Avatar } from "./Avatar";
import { MessageResult } from "./Dialogs";
import { Icon, Ticks } from "./Icon";
import { drawer, engine, openChat, toast } from "./Main";
import { Archived, Confirm, Devices, NewChat, PrivacyPanel, ProfilePanel, Starred } from "./Panels";
import { StoragePanel } from "./Storage";
import { dismissInstall, install, installDismissed, installed, installPrompt } from "../pwa";
import { isNative } from "../platform";
import { NewGroup } from "./Groups";
import { BackupPanel, HistoryBanner, RestoreBanner } from "./Link";
import { CallsPanel } from "./Call";
import { ChannelDirectory, ChannelsTab, NewChannel, StatusPrivacyPanel, StatusTab } from "./Updates";
import { BroadcastIcon, CommunitiesTab, CommunityPanel, NewBroadcast, NewCommunity } from "./Communities";

/** Pinned first (most recently pinned on top), then by last activity. */
export function sortChats(list: Chat[]): Chat[] {
  return [...list].sort((a, b) => S.pinnedAt(b.jid) - S.pinnedAt(a.jid) || b.lastTs - a.lastTs);
}

export function Sidebar({ session }: { session: Session }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  useEffect(() => (loadLists(session.xmpp.jid), loadStickers(session.xmpp.jid), loadChatLock(session.xmpp.jid)), [session.xmpp.jid]);
  void lists.value; // re-filter when favourites / lists change
  const [menu, setMenu] = useState(false);
  // The menu closes on Escape and on a click elsewhere, like the others.
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenu(false);
    const onDown = (e: MouseEvent) => !(e.target as HTMLElement).closest(".bar .menu-wrap") && setMenu(false);
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => (window.removeEventListener("keydown", onKey), document.removeEventListener("mousedown", onDown));
  }, [menu]);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [found, setFound] = useState<Msg[]>([]);
  const me = session.user;

  const q = query.trim().toLowerCase();
  useEffect(() => {
    if (q.length < 2) return setFound([]);
    const t = setTimeout(() => void engine.value?.search(q).then(setFound), 200);
    return () => clearTimeout(t);
  }, [q]);

  const archivedCount = S.chats.value.filter((c) => S.isArchived(c.jid)).length;
  const list = sortChats(
    S.chats.value.filter((c) => {
      if (S.isChannel(c.jid)) return false; // channels live in their own tab
      if (S.lockedChats.value.has(c.jid)) return false; // under "Locked chats"
      if (!q && S.isArchived(c.jid)) return false;
      if (!inFilter(filter, c.jid, c.unread)) return false;
      if (!q) return true;
      return S.displayName(c.jid).toLowerCase().includes(q) || c.jid.includes(q.replace(/\D/g, "") || "\u0000");
    }),
  );

  const d = drawer.value;
  return (
    <aside class="sidebar">
      {d === "newChat" && <NewChat />}
      {d === "newGroup" && <NewGroup />}
      {d === "profile" && <ProfilePanel />}
      {d === "devices" && <Devices />}
      {d === "starred" && <Starred />}
      {d === "archived" && <Archived />}
      {d === "privacy" && <PrivacyPanel />}
      {d === "storage" && <StoragePanel />}
      {d === "backup" && <BackupPanel />}
      {d === "calls" && <CallsPanel />}
      {d === "statusPrivacy" && <StatusPrivacyPanel />}
      {d === "channelDirectory" && <ChannelDirectory />}
      {d === "newChannel" && <NewChannel />}
      {d === "newCommunity" && <NewCommunity />}
      {d === "community" && <CommunityPanel />}
      {d === "newBroadcast" && <NewBroadcast />}
      {d === "appearance" && <AppearancePanel />}

      <header class="bar">
        <button class="icon-btn avatar-btn" onClick={() => (drawer.value = "profile")} aria-label="Profile">
          <Avatar url={me.avatar_url} jid={me.jid} name={me.name} size={40} />
        </button>
        <div class="bar-actions">
          <button class="icon-btn" onClick={() => (drawer.value = "newChat")} aria-label="New chat" title="New chat">
            <Icon name="chat" />
          </button>
          <div class="menu-wrap">
            <button class="icon-btn" onClick={() => setMenu(!menu)} aria-label="Menu" aria-expanded={menu}>
              <Icon name="menu" />
            </button>
            {menu && (
              <ul class="menu" onClick={() => setMenu(false)} role="menu">
                <MenuItem icon="person" keys={`${MOD} ${ALT} P`} label="Profile" onClick={() => (drawer.value = "profile")} />
                <MenuItem icon="devices" label="Linked devices" onClick={() => (drawer.value = "devices")} />
                <MenuItem icon="lock" keys={`${MOD} ${ALT} ,`} label="Privacy" onClick={() => (drawer.value = "privacy")} />
                <MenuItem icon="smiley" label="Appearance" onClick={() => (drawer.value = "appearance")} />
                {!isNative && notifPerm.value === "default" && <MenuItem icon="bell" label="Turn on notifications" onClick={() => void askNotifications()} />}
                <MenuItem icon="download" label="Chat backup" onClick={() => (drawer.value = "backup")} />
                <MenuItem icon="document" label="Storage" onClick={() => (drawer.value = "storage")} />
                <MenuItem icon="phone" label="Calls" onClick={() => (drawer.value = "calls")} />
                <MenuItem icon="star" label="Starred messages" onClick={() => (drawer.value = "starred")} />
                <MenuItem icon="archive" label="Archived chats" onClick={() => (drawer.value = "archived")} />
                {installPrompt.value && !installed.value && <MenuItem icon="devices" label="Install app" onClick={() => void installApp()} />}
                <li class="menu-sep" role="separator" />
                <MenuItem icon="search" keys={`${MOD} K`} label="Command palette" onClick={() => (palette.value = true)} />
                <MenuItem icon="info" keys={`${MOD} /`} label="Keyboard shortcuts" onClick={() => (shortcutsHelp.value = true)} />
                <li class="menu-sep" role="separator" />
                {getAccounts()
                  .filter((a) => a.xmpp.jid !== session.xmpp.jid)
                  .map((a) => (
                    <MenuItem key={a.xmpp.jid} icon="person" label={`Switch to ${a.user.name || a.user.phone}`} onClick={() => switchAccount(a.xmpp.jid)} />
                  ))}
                <MenuItem icon="personAdd" label="Add account" onClick={() => (S.addingAccount.value = true)} />
                <MenuItem icon="logout" label="Log out" onClick={() => setConfirmLogout(true)} />
              </ul>
            )}
          </div>
        </div>
      </header>

      {confirmLogout && (
        <Confirm
          text={`Log out of ${me.name || me.phone} on this device? Its chats here are backed up, then removed from this device. Your other devices${getAccounts().length > 1 ? " and other accounts here" : ""} stay logged in.`}
          action="Log out"
          onCancel={() => setConfirmLogout(false)}
          onConfirm={() => void logout()}
        />
      )}

      <ConnectionBanner />
      <NotificationBanner />
      <HistoryBanner />
      <RestoreBanner />
      <Tabs />

      {S.tab.value === "status" ? (
        <StatusTab />
      ) : S.tab.value === "channels" ? (
        <ChannelsTab />
      ) : S.tab.value === "communities" ? (
        <CommunitiesTab />
      ) : (
      <>
      <div class="search">
        <Icon name="search" size={20} />
        <input value={query} onInput={(e) => setQuery(e.currentTarget.value)} placeholder="Search or start new chat" aria-label="Search chats and messages" />
        {query && (
          <button class="icon-btn small" onClick={() => setQuery("")} aria-label="Clear search">
            <Icon name="close" size={18} />
          </button>
        )}
      </div>
      <InstallBanner />
      <FilterChips filter={filter} onChange={setFilter} />

      <ul class="chat-list" role="listbox" aria-label="Chats">
        {S.showLocked.value && S.lockOpen.value ? (
          <>
            <li class="list-heading locked-heading">
              <Icon name="lock" size={14} /> Locked chats
              <button class="link" onClick={() => ((S.showLocked.value = false), (S.lockOpen.value = false))} aria-label="Lock again">
                Lock
              </button>
            </li>
            {sortChats(S.chats.value.filter((c) => S.lockedChats.value.has(c.jid))).map((c) => (
              <ChatRow key={c.jid} chat={c} />
            ))}
          </>
        ) : (
          !q &&
          S.lockedChats.value.size > 0 && (
            <li class="archived-row locked-row" tabIndex={0} onClick={() => (lockDialog.value = { mode: "enter" })} onKeyDown={(e) => e.key === "Enter" && (lockDialog.value = { mode: "enter" })}>
              <span class="archived-icon">
                <Icon name="lock" />
              </span>
              <span class="grow">Locked chats</span>
            </li>
          )
        )}
        {!q && archivedCount > 0 && (
          <li class="archived-row" tabIndex={0} onClick={() => (drawer.value = "archived")} onKeyDown={(e) => e.key === "Enter" && (drawer.value = "archived")}>
            <span class="archived-icon">
              <Icon name="archive" />
            </span>
            <span class="grow">Archived</span>
            <span class="muted small">{archivedCount}</span>
          </li>
        )}
        {q && <NumberResult query={query} />}
        {q && list.length > 0 && <li class="list-heading">Chats</li>}
        {list.map((c) => (
          <ChatRow key={c.jid} chat={c} />
        ))}
        {q && found.length > 0 && <li class="list-heading">Messages</li>}
        {q &&
          found.filter((m) => !S.lockedChats.value.has(m.chat) || S.lockOpen.value).map((m) => (
            <MessageResult key={m.key} m={m} onOpen={() => void engine.value?.openChat(m.chat, m)} />
          ))}
        {!list.length && !found.length && !phoneQuery(query) && (
          <li class="empty">
            {q ? "No chats, contacts or messages found." : filter !== "all" ? `No chats in ${filterLabel(filter)}.` : S.syncing.value ? "Syncing your chats…" : "No chats yet. Start one with the chat icon above."}
          </li>
        )}
      </ul>
      </>
      )}
    </aside>
  );
}

/** A typed phone number -> E.164, using our own country code for local numbers. */
export function phoneQuery(q: string): string | null {
  const t = q.trim();
  if (!/^\+?[\d\s().-]{6,20}$/.test(t)) return null;
  const digits = t.replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) return null;
  if (t.startsWith("+") || digits.length > 10) return "+" + digits;
  const me = S.myJid.value.split("@")[0];
  const cc = me.length > 10 ? me.slice(0, me.length - 10) : "91";
  return "+" + cc + digits.replace(/^0+/, "");
}

/** Search box holding a phone number: find that person on xmm and chat. */
const installApp = () => void install().then((ok) => ok && toast("xmm is installed: find it with your other apps"));

/** "Install xmm" (the web app, when the browser offers it). */
function InstallBanner() {
  if (!installPrompt.value || installed.value || installDismissed.value) return null;
  return (
    <div class="install-banner" role="region" aria-label="Install xmm">
      <span class="install-icon">
        <img src="/icon.svg" alt="" width={32} height={32} />
      </span>
      <span class="grow small">
        <strong>Install xmm</strong>
        <span class="muted"> · its own window and an unread badge</span>
      </span>
      <button class="chip on" onClick={installApp}>
        Install
      </button>
      <button class="icon-btn" onClick={dismissInstall} aria-label="Not now">
        <Icon name="close" size={18} />
      </button>
    </div>
  );
}

export function NumberResult({ query, onOpen }: { query: string; onOpen?: () => void }) {
  // A phone number, or an @username.
  const handle = /^@([a-z][a-z0-9._]{2,29})$/i.exec(query.trim())?.[1]?.toLowerCase();
  const phone = phoneQuery(query) ?? (handle ? "@" + handle : null);
  const [state, setState] = useState<{ phone: string; jid?: string; name?: string; avatar?: string | null; missing?: boolean } | null>(null);
  useEffect(() => {
    if (!phone) return setState(null);
    let live = true;
    const find = phone.startsWith("@")
      ? api.userByUsername(phone).then(({ user }) => [user], (e) => (e instanceof ApiError && e.status === 404 ? [] : Promise.reject(e)))
      : null;
    const t = setTimeout(
      () =>
        void (find ?? api.lookupContacts([phone]).then((r) => r.users)).then(
          (users) => live && setState(users[0] ? { phone, jid: users[0].jid, name: users[0].name, avatar: users[0].avatar_url } : { phone, missing: true }),
          () => live && setState(null),
        ),
      250,
    );
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [phone]);
  if (!phone || state?.phone !== phone) return null;
  if (state.missing && phone.startsWith("@"))
    return (
      <li class="number-result missing">
        <span class="list-action-icon">
          <Icon name="person" />
        </span>
        <span class="grow">
          <span class="chat-name">{phone}</span>
          <small class="muted">No one has this username</small>
        </span>
      </li>
    );
  if (state.missing)
    return (
      <li class="number-result missing">
        <span class="list-action-icon">
          <Icon name="person" />
        </span>
        <span class="grow">
          <span class="chat-name">{phone}</span>
          <small class="muted">isn't on xmm yet</small>
        </span>
        <button
          class="chip"
          onClick={() => void navigator.clipboard?.writeText(`Let's chat on xmm: ${location.origin}`).then(() => toast("Invite link copied"))}
        >
          Invite
        </button>
      </li>
    );
  const jid = state.jid!;
  const open = () => {
    onOpen?.();
    S.tab.value = "chats";
    void engine.value?.openChat(jid);
  };
  return (
    <li class="number-result" tabIndex={0} role="option" onClick={open} onKeyDown={(e) => e.key === "Enter" && open()} aria-label={"Chat with " + phone}>
      <Avatar url={state.avatar} jid={jid} name={S.displayName(jid)} />
      <span class="grow">
        <span class="chat-name">{S.roster.value.get(jid)?.name || phone}</span>
        <small class="muted">{state.name ? `~${state.name} · ` : ""}Tap to chat</small>
      </span>
      <Icon name="chat" />
    </li>
  );
}

/** Chats · Status · Channels · Communities. */
function Tabs() {
  const t = S.tab.value;
  const chatsUnread = S.chats.value.filter((c) => c.unread && !S.isChannel(c.jid) && !S.isMuted(c.jid) && !S.isArchived(c.jid)).length;
  const channelUnread = S.chats.value.some((c) => c.unread && S.isChannel(c.jid));
  const newStatus = [...S.statuses.value.values()].some((l) => l.some((s) => !s.viewed && s.expiresAt > Date.now()));
  const tab = (id: typeof t, label: string, icon: string, badge?: number | boolean) => (
    <button role="tab" aria-selected={t === id} class={"tab" + (t === id ? " on" : "")} onClick={() => (S.tab.value = id)}>
      <Icon name={icon} size={20} />
      <span>{label}</span>
      {typeof badge === "number" ? badge > 0 && <span class="badge">{badge}</span> : badge && <span class="dot" aria-label="new" />}
    </button>
  );
  return (
    <nav class="tabs" role="tablist" aria-label="Sections">
      {tab("chats", "Chats", "chat", chatsUnread)}
      {tab("status", "Status", "status", newStatus)}
      {tab("channels", "Channels", "campaign", channelUnread)}
      {tab("communities", "Communities", "group")}
    </nav>
  );
}

export function ChatRow({ chat }: { chat: Chat }) {
  const jid = chat.jid;
  const name = S.displayName(jid);
  const group = S.isGroup(jid);
  const broadcast = S.isBroadcast(jid);
  const avatar = group ? S.groups.value.get(jid)?.avatar_url : S.profiles.value.get(jid)?.avatar_url;
  const who = group && S.typingWho.value.get(jid);
  // Groups prefix the preview with the author: "Ben: see you there".
  const author = group && chat.lastFrom && chat.lastFrom !== "system" && chat.lastBody ? (chat.lastDir === "out" ? "" : S.memberName(chat.lastFrom) + ": ") : "";
  const active = S.activeChat.value === jid;
  const typing = S.isTyping(jid);
  const muted = S.isMuted(jid);
  const pinned = !!S.pinnedAt(jid);
  const archived = S.isArchived(jid);
  const [menu, setMenu] = useState(false);
  // Gestures: swipe left = archive / unarchive, swipe right = pin / unpin,
  // long-press or right-click = chat menu (mouse drags swipe too here).
  const act = (p: Parameters<NonNullable<typeof engine.value>["setChat"]>[1], done: string) =>
    void engine.value?.setChat(jid, p).then(() => toast(done), (err) => toast(String(err.message ?? err)));
  const g = useGestures({
    onSwipeLeft: () => act({ archived: !archived }, archived ? "Chat unarchived" : "Chat archived"),
    onSwipeRight: () => act({ pinned: !pinned }, pinned ? "Chat unpinned" : "Chat pinned"),
    onLongPress: () => setMenu(true),
    mouseSwipe: true,
  });
  return (
    <li
      class={"chat-row swipeable" + (active ? " active" : "")}
      data-jid={jid}
      data-menu={menu ? "" : undefined}
      role="option"
      aria-selected={active}
      tabIndex={0}
      onClick={() => openChat(jid)}
      onKeyDown={(e) => e.key === "Enter" && openChat(jid)}
      {...g.bind}
    >
      <span class="swipe-bg bg-right" aria-hidden="true">
        <Icon name="pin" size={20} />
        {pinned ? "Unpin" : "Pin"}
      </span>
      <span class="swipe-bg bg-left" aria-hidden="true">
        {archived ? "Unarchive" : "Archive"}
        <Icon name="archive" size={20} />
      </span>
      <div class="swipe-move row-slide" ref={g.target as preact.Ref<HTMLDivElement>}>
      {broadcast ? <BroadcastIcon /> : <Avatar url={avatar} jid={jid} name={name} group={group} />}
      <div class="chat-row-main">
        <div class="chat-row-top">
          <span class="chat-name">{name}</span>
          <span class={"chat-time" + (chat.unread && !muted ? " unread" : "")}>{listTime(chat.lastTs)}</span>
        </div>
        <div class="chat-row-bottom">
          <span class="chat-preview">
            {typing ? (
              <em class="typing">{who ? `${S.memberName(who)} is typing…` : "typing…"}</em>
            ) : chat.draft && !active ? (
              <>
                <span class="draft">Draft:</span> {chat.draft}
              </>
            ) : (
              <>
                {chat.lastDir === "out" && chat.lastBody && <Ticks status={S.shownStatus(jid, chat.lastStatus ?? Status.Sent)} />}
                {author}
                {chat.lastBody}
              </>
            )}
          </span>
          {chat.mention && chat.unread > 0 && <span class="badge mention">@</span>}
          {muted && <Icon name="bellOff" size={16} />}
          {pinned && <Icon name="pin" size={16} />}
          {chat.unread > 0 && <span class={"badge" + (muted ? " muted" : "")}>{chat.unread}</span>}
          <button
            class="row-menu-btn"
            aria-label="Chat options"
            onClick={(e) => {
              e.stopPropagation();
              setMenu(!menu);
            }}
          >
            <Icon name="down" size={20} />
          </button>
        </div>
      </div>
      </div>
      {menu && <ChatMenu jid={jid} onClose={() => setMenu(false)} />}
    </li>
  );
}

/** A menu entry: icon, label and (as a hint, outside the label text) its shortcut. */
function MenuItem({ icon, label, keys, onClick }: { icon: string; label: string; keys?: string; onClick: () => void }) {
  return (
    <li role="menuitem" onClick={onClick} data-keys={keys}>
      <Icon name={icon as "person"} size={18} />
      {label}
    </li>
  );
}
const MAC = /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = MAC ? "⌘" : "Ctrl";
const ALT = MAC ? "⌥" : "Alt";

function ChatMenu({ jid, onClose }: { jid: string; onClose: () => void }) {
  const e = engine.value!;
  const ref = useRef<HTMLUListElement>(null);
  const [mute, setMute] = useState(false);
  useEffect(() => {
    // Pointer events: the mouse events a long-press ends with (after the
    // menu opened) mustn't close it again.
    const onDown = (ev: PointerEvent) => ref.current && !ref.current.contains(ev.target as Node) && onClose();
    const t = setTimeout(() => document.addEventListener("pointerdown", onDown));
    return () => (clearTimeout(t), document.removeEventListener("pointerdown", onDown));
  }, []);
  const run = (p: Parameters<typeof e.setChat>[1]) => (ev: Event) => {
    ev.stopPropagation();
    onClose();
    e.setChat(jid, p).catch((err) => toast(err.message ?? String(err)));
  };
  const flip = useFlip(ref, ".chat-list", 48);
  const muted = S.isMuted(jid);
  const now = Math.floor(Date.now() / 1000);
  return (
    <ul
      class={"menu chat-menu" + (flip.up ? " up" : "")}
      ref={ref}
      role="menu"
      onClick={(ev) => ev.stopPropagation()}
      style={flip.maxHeight ? { maxHeight: `${flip.maxHeight}px`, overflowY: "auto" } : undefined}
    >
      <li role="menuitem" onClick={() => (onClose(), toggleLock(jid))}>
        {S.lockedChats.value.has(jid) ? "Unlock chat" : "Lock chat"}
      </li>
      <li role="menuitem" onClick={() => (onClose(), toggleFavourite(jid))}>
        {isFavourite(jid) ? "Remove from favourites" : "Add to favourites"}
      </li>
      {lists.value.lists.map((l) => (
        <li key={l.id} role="menuitem" onClick={() => (onClose(), toggleInList(l.id, jid))}>
          {l.chats.includes(jid) ? `Remove from ${l.name}` : `Add to ${l.name}`}
        </li>
      ))}
      <li role="menuitem" onClick={run({ archived: !S.isArchived(jid) })}>
        {S.isArchived(jid) ? "Unarchive chat" : "Archive chat"}
      </li>
      {muted ? (
        <li role="menuitem" onClick={run({ muted_until: 0 })}>
          Unmute notifications
        </li>
      ) : mute ? (
        <>
          <li role="menuitem" onClick={run({ muted_until: now + 8 * 3600 })}>
            Mute for 8 hours
          </li>
          <li role="menuitem" onClick={run({ muted_until: now + 7 * 86400 })}>
            Mute for 1 week
          </li>
          <li role="menuitem" onClick={run({ muted_until: -1 })}>
            Mute always
          </li>
        </>
      ) : (
        <li role="menuitem" onClick={() => setMute(true)}>
          Mute notifications…
        </li>
      )}
      <li role="menuitem" onClick={run({ pinned: !S.pinnedAt(jid) })}>
        {S.pinnedAt(jid) ? "Unpin chat" : "Pin chat"}
      </li>
      <li
        role="menuitem"
        class="danger"
        onClick={(ev) => {
          ev.stopPropagation();
          onClose();
          void e.deleteChat(jid);
        }}
      >
        Delete chat
      </li>
    </ul>
  );
}

function ConnectionBanner() {
  const c = S.conn.value;
  if (c === "online" && !S.syncing.value) return null;
  return (
    <div class={"conn-banner " + c}>
      {c === "online" ? "Syncing messages…" : c === "connecting" ? "Connecting…" : "Computer not connected. Make sure your internet is active."}
    </div>
  );
}

/** Browser notification permission, kept current (it can change in site settings). */
export const notifPerm = signal<NotificationPermission | "unsupported">(typeof Notification === "undefined" ? "unsupported" : Notification.permission);
if (typeof navigator !== "undefined" && typeof Notification !== "undefined")
  void navigator.permissions
    ?.query({ name: "notifications" })
    .then((st) => (st.onchange = () => (notifPerm.value = Notification.permission)))
    .catch(() => {});

/** Ask for notifications (needs a click or key press) and register this browser for push. */
export async function askNotifications() {
  if (typeof Notification === "undefined" || Notification.permission !== "default") return;
  const p = await Notification.requestPermission();
  notifPerm.value = p;
  if (p === "granted") void engine.value?.enablePush().catch((e) => console.warn("push", e));
}

const BANNER_KEY = "xmm.notifBanner";
function NotificationBanner() {
  const perm = notifPerm.value;
  const [hidden, setHidden] = useState(() => {
    try {
      // Dismissed: back after a week (it used to be gone for good).
      return Number(localStorage.getItem(BANNER_KEY) ?? 0) > Date.now() - 7 * 86_400_000;
    } catch {
      return false;
    }
  });
  if (isNative || perm === "granted" || hidden) return null;
  const insecure = typeof window !== "undefined" && !window.isSecureContext;
  return (
    <div class="notif-banner">
      <span class="notif-icon">
        <Icon name="bellOff" />
      </span>
      <div class="grow">
        <strong>Get notified of new messages</strong>
        {insecure ? (
          <span class="notif-help">Notifications need HTTPS: open xmm through its https:// address.</span>
        ) : perm === "denied" || perm === "unsupported" ? (
          <span class="notif-help">{perm === "denied" ? "Notifications are blocked. Allow them in the site settings (the icon left of the address), then reload." : "This browser can't show notifications."}</span>
        ) : (
          <button class="link" onClick={() => void askNotifications()}>
            Turn on desktop notifications ›
          </button>
        )}
      </div>
      <button
        class="icon-btn small"
        aria-label="Dismiss"
        onClick={() => {
          setHidden(true);
          try {
            localStorage.setItem(BANNER_KEY, String(Date.now()));
          } catch {
            /* ignore */
          }
        }}
      >
        <Icon name="close" size={18} />
      </button>
    </div>
  );
}

async function logout() {
  const e = engine.value;
  const db = e?.db.name;
  // This device's chats go into the account's backup before they're wiped.
  await Promise.race([e?.backupNow(), new Promise((r) => setTimeout(r, 15_000))]).catch(() => {});
  await e?.stop();
  try {
    await api.logout();
  } finally {
    if (db) {
      indexedDB.deleteDatabase(db);
      // Private encryption keys of this device go too.
      indexedDB.deleteDatabase(db.replace(/^xmm:/, "xmm-e2e:"));
    }
    // Cached photos/voice notes belong to this account: wipe them too.
    navigator.serviceWorker?.controller?.postMessage("clear-media");
    void caches?.delete("xmm-media");
  }
}
